import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Edit2, Trash2, Play, Globe, Loader2, FileVideo, FileAudio, FileImage, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { SchedulingUpgradeCard } from '../ui/SchedulingUpgradeCard';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useSchedulingFeature } from '../../context/SchedulingFeatureContext';
import { useToast } from '../../hooks/use-toast';
import { format, formatDistanceToNow } from 'date-fns';

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'UTC', label: 'UTC' },
];

const ScheduledPostsManager = () => {
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPost, setEditingPost] = useState(null);
  const [newDate, setNewDate] = useState('');
  const [newTime, setNewTime] = useState('');
  const [newTimezone, setNewTimezone] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  
  const { token } = useAuth();
  const { isDark } = useTheme();
  const { toast } = useToast();
  const { hasSchedulingAccess, loading: featureLoading, SCHEDULING_TIER_NAME } = useSchedulingFeature();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (hasSchedulingAccess) {
      fetchScheduledPosts();
    } else {
      setLoading(false);
    }
  }, [hasSchedulingAccess]);

  const fetchScheduledPosts = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/content/scheduled`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch');
      
      const data = await response.json();
      setScheduledPosts(data.scheduled_posts || []);
    } catch (error) {
      console.error('Error fetching scheduled posts:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load scheduled posts'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (post) => {
    setEditingPost(post);
    const scheduled = new Date(post.scheduled_at);
    setNewDate(scheduled.toISOString().split('T')[0]);
    setNewTime(scheduled.toTimeString().slice(0, 5));
    setNewTimezone(post.schedule_timezone || 'UTC');
  };

  const handleUpdateSchedule = async () => {
    if (!editingPost || !newDate || !newTime) return;
    
    setActionLoading(editingPost.id);
    try {
      const formData = new FormData();
      formData.append('scheduled_at', new Date(`${newDate}T${newTime}`).toISOString());
      formData.append('schedule_timezone', newTimezone);
      
      const response = await fetch(`${API_URL}/api/content/scheduled/${editingPost.id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      if (!response.ok) throw new Error('Failed to update');
      
      toast({
        title: 'Schedule updated',
        description: 'Your post has been rescheduled'
      });
      
      setEditingPost(null);
      fetchScheduledPosts();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to update schedule'
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancelPost = async (postId) => {
    if (!window.confirm('Are you sure you want to cancel this scheduled post? This action cannot be undone.')) {
      return;
    }
    
    setActionLoading(postId);
    try {
      const response = await fetch(`${API_URL}/api/content/scheduled/${postId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to cancel');
      
      toast({
        title: 'Post cancelled',
        description: 'The scheduled post has been removed'
      });
      
      fetchScheduledPosts();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to cancel post'
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handlePublishNow = async (postId) => {
    if (!window.confirm('Publish this post immediately?')) {
      return;
    }
    
    setActionLoading(postId);
    try {
      const response = await fetch(`${API_URL}/api/content/scheduled/${postId}/publish-now`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to publish');
      
      toast({
        title: 'Published!',
        description: 'Your post is now live'
      });
      
      fetchScheduledPosts();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to publish post'
      });
    } finally {
      setActionLoading(null);
    }
  };

  const getContentIcon = (contentType) => {
    if (contentType?.includes('video')) return <FileVideo className="w-5 h-5 text-blue-400" />;
    if (contentType?.includes('audio')) return <FileAudio className="w-5 h-5 text-purple-400" />;
    if (contentType?.includes('image')) return <FileImage className="w-5 h-5 text-green-400" />;
    return <Calendar className="w-5 h-5 text-gray-400" />;
  };

  if (loading || featureLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-yellow-400" />
      </div>
    );
  }

  // Show upgrade UI if user doesn't have scheduling access
  if (!hasSchedulingAccess) {
    return (
      <div className="space-y-6" role="region" aria-label="Scheduled posts - locked">
        <div className="flex items-center justify-between">
          <div>
            <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Scheduled Posts
            </h3>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Schedule posts to publish automatically
            </p>
          </div>
        </div>
        
        <SchedulingUpgradeCard 
          variant="banner" 
          placement="settings_posting"
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Scheduled Posts
          </h3>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Manage your upcoming scheduled content
          </p>
        </div>
        <Badge variant="outline" className="text-yellow-400 border-yellow-400/50">
          {scheduledPosts.length} scheduled
        </Badge>
      </div>

      {scheduledPosts.length === 0 ? (
        <Card className={isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'}>
          <CardContent className="py-12 text-center">
            <Calendar className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
            <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>
              No scheduled posts
            </p>
            <p className={`text-sm mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              Schedule your first post when uploading new content
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {scheduledPosts.map((post) => {
            const scheduledDate = new Date(post.scheduled_at);
            const isLoading = actionLoading === post.id;
            
            return (
              <Card 
                key={post.id} 
                className={`${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'} hover:border-yellow-500/50 transition-colors`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Thumbnail or Icon */}
                    <div className={`w-16 h-16 rounded-lg flex items-center justify-center ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      {post.thumbnail_url ? (
                        <img 
                          src={post.thumbnail_url} 
                          alt={post.title}
                          className="w-full h-full object-cover rounded-lg"
                        />
                      ) : (
                        getContentIcon(post.content_type)
                      )}
                    </div>

                    {/* Content Info */}
                    <div className="flex-1 min-w-0">
                      <h4 className={`font-semibold truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {post.title}
                      </h4>
                      
                      <div className={`flex items-center gap-4 mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {format(scheduledDate, 'MMM d, yyyy')}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {format(scheduledDate, 'h:mm a')}
                        </span>
                        <span className="flex items-center gap-1">
                          <Globe className="w-4 h-4" />
                          {post.schedule_timezone || 'UTC'}
                        </span>
                      </div>
                      
                      <p className={`text-xs mt-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        Publishing {formatDistanceToNow(scheduledDate, { addSuffix: true })}
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handlePublishNow(post.id)}
                        disabled={isLoading}
                        className={isDark ? 'border-green-500/50 text-green-400 hover:bg-green-500/10' : 'border-green-500 text-green-600 hover:bg-green-50'}
                      >
                        {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditClick(post)}
                        disabled={isLoading}
                        className={isDark ? 'border-gray-600' : 'border-gray-300'}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleCancelPost(post.id)}
                        disabled={isLoading}
                        className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Edit Schedule Dialog */}
      <Dialog open={!!editingPost} onOpenChange={() => setEditingPost(null)}>
        <DialogContent className={isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : 'text-gray-900'}>
              Reschedule Post
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>Date</Label>
              <Input
                type="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className={isDark ? 'bg-gray-800 border-gray-600 text-white' : ''}
              />
            </div>
            
            <div className="space-y-2">
              <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>Time</Label>
              <Input
                type="time"
                value={newTime}
                onChange={(e) => setNewTime(e.target.value)}
                className={isDark ? 'bg-gray-800 border-gray-600 text-white' : ''}
              />
            </div>
            
            <div className="space-y-2">
              <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>Timezone</Label>
              <Select value={newTimezone} onValueChange={setNewTimezone}>
                <SelectTrigger className={isDark ? 'bg-gray-800 border-gray-600 text-white' : ''}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className={isDark ? 'bg-gray-800 border-gray-700' : ''}>
                  {TIMEZONES.map((tz) => (
                    <SelectItem key={tz.value} value={tz.value} className={isDark ? 'text-white' : ''}>
                      {tz.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingPost(null)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateSchedule}
              disabled={!newDate || !newTime || actionLoading}
              className="bg-gradient-to-r from-yellow-400 to-purple-500"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Update Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ScheduledPostsManager;
